import {
  Alert,
  Badge,
  Button,
  Divider,
  Group,
  Loader,
  Menu,
  Modal,
  MultiSelect,
  Radio,
  SegmentedControl,
  Stack,
  Text,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  IconBan,
  IconChevronDown,
  IconGitMerge,
  IconTag,
  IconTrash,
  IconUsers,
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { trpc } from '../lib/trpc';

type Option = { id: number; title: string };

/**
 * Bulk-action toolbar for the contacts list. Rendered only when ≥1 row is
 * selected and the user can manage contacts. Wraps the four bulk mutations the
 * contacts router already exposes (delete / assign labels / assign groups /
 * set marketing opt-out); `onDone` refreshes the list and clears the parent's
 * selection after any successful action.
 */
export function BulkContactActions(props: {
  selectedIds: number[];
  labels: Option[];
  groups: Option[];
  onDone: () => void;
}) {
  const { selectedIds, labels, groups, onDone } = props;
  const count = selectedIds.length;

  const [labelsOpen, labelsCtl] = useDisclosure(false);
  const [groupsOpen, groupsCtl] = useDisclosure(false);
  const [optOutOpen, optOutCtl] = useDisclosure(false);

  const [labelIds, setLabelIds] = useState<string[]>([]);
  const [groupIds, setGroupIds] = useState<string[]>([]);
  const [consent, setConsent] = useState('out');

  const assignLabels = trpc.contacts.contacts.bulkAssignLabels.useMutation({
    onSuccess: (r) => {
      notifications.show({ message: `Added ${r.added} label links`, color: 'green' });
      labelsCtl.close();
      setLabelIds([]);
      onDone();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const assignGroups = trpc.contacts.contacts.bulkAssignGroups.useMutation({
    onSuccess: (r) => {
      notifications.show({ message: `Added ${r.added} group links`, color: 'green' });
      groupsCtl.close();
      setGroupIds([]);
      onDone();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const setOptOut = trpc.contacts.contacts.bulkSetOptOut.useMutation({
    onSuccess: (r) => {
      notifications.show({ message: `Updated ${r.updated} contacts`, color: 'green' });
      optOutCtl.close();
      onDone();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const remove = trpc.contacts.contacts.bulkDelete.useMutation({
    onSuccess: (r) => {
      notifications.show({ message: `Deleted ${r.deleted} contacts`, color: 'green' });
      onDone();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  // ── Merge (multi-channel stitching) ──────────────────────────────
  const [mergeOpen, mergeCtl] = useDisclosure(false);
  const [survivorId, setSurvivorId] = useState<number | null>(null);
  const canMerge = count >= 2 && count <= 5;

  const mergePreview = trpc.contacts.contacts.mergePreview.useQuery(
    { contactIds: selectedIds },
    { enabled: mergeOpen && canMerge },
  );
  useEffect(() => {
    if (survivorId === null && mergePreview.data && mergePreview.data.contacts.length > 0) {
      // Default to the contact with the richest history (most messages).
      const best = [...mergePreview.data.contacts].sort(
        (a, b) => b.messageCount - a.messageCount,
      )[0];
      if (best) setSurvivorId(best.id);
    }
  }, [mergePreview.data, survivorId]);

  const closeMerge = () => {
    mergeCtl.close();
    setSurvivorId(null);
  };
  const mergeMut = trpc.contacts.contacts.merge.useMutation({
    onSuccess: (r) => {
      notifications.show({
        message: `Merged ${r.mergedCount + 1} contacts into one`,
        color: 'green',
      });
      closeMerge();
      onDone();
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });

  return (
    <>
      {/* All bulk actions collapse into one dropdown so the toolbar never
          clips on narrow screens (the inline-button row used to overflow). */}
      <Menu shadow="md" width={232} position="bottom-start" withinPortal>
        <Menu.Target>
          <Button size="sm" variant="light" rightSection={<IconChevronDown size={14} />}>
            Bulk actions ({count})
          </Button>
        </Menu.Target>
        <Menu.Dropdown>
          <Menu.Label>{count} selected</Menu.Label>
          <Menu.Item leftSection={<IconTag size={14} />} onClick={labelsCtl.open}>
            Label
          </Menu.Item>
          <Menu.Item leftSection={<IconUsers size={14} />} onClick={groupsCtl.open}>
            Group
          </Menu.Item>
          <Menu.Item color="orange" leftSection={<IconBan size={14} />} onClick={optOutCtl.open}>
            Marketing consent
          </Menu.Item>
          {canMerge && (
            <Menu.Item
              color="grape"
              leftSection={<IconGitMerge size={14} />}
              onClick={mergeCtl.open}
            >
              Merge {count}
            </Menu.Item>
          )}
          <Menu.Divider />
          <Menu.Item
            color="red"
            leftSection={<IconTrash size={14} />}
            disabled={remove.isPending}
            onClick={() => remove.mutate({ contactIds: selectedIds })}
          >
            Delete {count}
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>

      {/* Assign labels */}
      <Modal
        opened={labelsOpen}
        onClose={labelsCtl.close}
        title={`Assign labels to ${count} contacts`}
        centered
      >
        <Stack>
          <MultiSelect
            label="Labels"
            placeholder="Pick one or more labels"
            data={labels.map((l) => ({ value: String(l.id), label: l.title }))}
            value={labelIds}
            onChange={setLabelIds}
            searchable
          />
          <Group justify="end">
            <Button variant="default" onClick={labelsCtl.close}>
              Cancel
            </Button>
            <Button
              loading={assignLabels.isPending}
              disabled={labelIds.length === 0}
              onClick={() =>
                assignLabels.mutate({ contactIds: selectedIds, labelIds: labelIds.map(Number) })
              }
            >
              Assign
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Assign groups */}
      <Modal
        opened={groupsOpen}
        onClose={groupsCtl.close}
        title={`Add ${count} contacts to groups`}
        centered
      >
        <Stack>
          <MultiSelect
            label="Groups"
            placeholder="Pick one or more groups"
            data={groups.map((g) => ({ value: String(g.id), label: g.title }))}
            value={groupIds}
            onChange={setGroupIds}
            searchable
          />
          <Group justify="end">
            <Button variant="default" onClick={groupsCtl.close}>
              Cancel
            </Button>
            <Button
              loading={assignGroups.isPending}
              disabled={groupIds.length === 0}
              onClick={() =>
                assignGroups.mutate({ contactIds: selectedIds, groupIds: groupIds.map(Number) })
              }
            >
              Add
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Marketing opt-out / opt-in */}
      <Modal
        opened={optOutOpen}
        onClose={optOutCtl.close}
        title={`Marketing consent for ${count} contacts`}
        centered
      >
        <Stack>
          <SegmentedControl
            value={consent}
            onChange={setConsent}
            data={[
              { label: 'Opt out', value: 'out' },
              { label: 'Opt in', value: 'in' },
            ]}
          />
          <Text size="sm" c="dimmed">
            {consent === 'out'
              ? 'Selected contacts will be suppressed from WhatsApp marketing campaigns.'
              : 'Selected contacts will be re-enabled for WhatsApp marketing campaigns.'}
          </Text>
          <Group justify="end">
            <Button variant="default" onClick={optOutCtl.close}>
              Cancel
            </Button>
            <Button
              color={consent === 'out' ? 'orange' : 'blue'}
              loading={setOptOut.isPending}
              onClick={() =>
                setOptOut.mutate({
                  contactIds: selectedIds,
                  optedOut: consent === 'out',
                  source: 'manual',
                })
              }
            >
              Confirm
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Merge contacts into one */}
      <Modal opened={mergeOpen} onClose={closeMerge} title="Merge contacts" centered size="lg">
        {mergePreview.isPending ? (
          <Group justify="center" p="xl">
            <Loader />
          </Group>
        ) : mergePreview.data ? (
          <Stack>
            <Text size="sm" c="dimmed">
              Pick the contact to <b>keep</b>. The others are merged into it — their messages,
              labels, groups, notes, and channel identities move over — then deleted. This
              can&apos;t be undone.
            </Text>
            <Radio.Group
              value={survivorId ? String(survivorId) : null}
              onChange={(v) => setSurvivorId(Number(v))}
            >
              <Stack gap="xs">
                {mergePreview.data.contacts.map((c) => (
                  <Radio.Card key={c.id} value={String(c.id)} p="sm" withBorder radius="md">
                    <Group wrap="nowrap" align="flex-start" gap="sm">
                      <Radio.Indicator />
                      <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                        <Group gap="xs">
                          <Text fw={600} truncate>
                            {c.fullName}
                          </Text>
                          {c.channels.includes('whatsapp') && (
                            <Badge size="xs" color="green" variant="light">
                              WA
                            </Badge>
                          )}
                          {c.channels.includes('messenger') && (
                            <Badge size="xs" color="blue" variant="light">
                              FB
                            </Badge>
                          )}
                          {c.channels.includes('instagram') && (
                            <Badge size="xs" color="pink" variant="light">
                              IG
                            </Badge>
                          )}
                        </Group>
                        <Text size="xs" c="dimmed">
                          {[
                            c.waIdDisplay || null,
                            c.instagramUsername ? `@${c.instagramUsername}` : null,
                            c.email,
                          ]
                            .filter(Boolean)
                            .join(' · ') || '—'}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {c.messageCount} messages · {c.labelCount} labels · {c.groupCount} groups
                          · {c.noteCount} notes
                        </Text>
                      </Stack>
                    </Group>
                  </Radio.Card>
                ))}
              </Stack>
            </Radio.Group>
            {mergePreview.data.conflicts.length > 0 && (
              <Alert color="yellow" variant="light" title="Heads up">
                <Stack gap={4}>
                  {mergePreview.data.conflicts.map((c) => (
                    <Text key={c} size="xs">
                      {c}
                    </Text>
                  ))}
                </Stack>
              </Alert>
            )}
            <Divider />
            <Group justify="space-between">
              <Text size="xs" c="dimmed">
                {count} contacts → 1
              </Text>
              <Group>
                <Button variant="default" onClick={closeMerge}>
                  Cancel
                </Button>
                <Button
                  color="grape"
                  leftSection={<IconGitMerge size={14} />}
                  loading={mergeMut.isPending}
                  disabled={!survivorId}
                  onClick={() =>
                    survivorId &&
                    mergeMut.mutate({
                      survivorId,
                      mergeIds: selectedIds.filter((id) => id !== survivorId),
                    })
                  }
                >
                  Merge into kept
                </Button>
              </Group>
            </Group>
          </Stack>
        ) : (
          <Text c="red" size="sm">
            Could not load the merge preview.
          </Text>
        )}
      </Modal>
    </>
  );
}
